import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Mail, Phone, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600"
          >
            <ArrowLeft size={20} />
            Voltar ao Início
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center">
              <Shield className="text-orange-600" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Política de Privacidade</h1>
              <p className="text-gray-600 mt-1">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none space-y-8">
            {/* Introdução */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introdução</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A <strong>Somar Distâncias, Lda.</strong> (NIF: 510210848), com sede na Estrada de Santa Eulália, Horta Preta, 
                Casal das Oliveiras, 2670-351 Loures, Portugal, compromete-se a proteger a privacidade e os dados pessoais 
                dos seus utilizadores em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD) e demais 
                legislação aplicável em Portugal.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Esta Política de Privacidade explica como recolhemos, utilizamos, armazenamos e protegemos os seus dados 
                pessoais quando visita o nosso website ou utiliza os nossos serviços.
              </p>
            </section>

            {/* Responsável pelo tratamento */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Responsável pelo Tratamento de Dados</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <p className="font-semibold text-gray-900 mb-3">Somar Distâncias, Lda.</p>
                <div className="space-y-2 text-gray-700">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-orange-600 flex-shrink-0 mt-1" size={18} />
                    <span>Estrada de Santa Eulália, Horta Preta, Casal das Oliveiras<br />2670-351 Loures, Portugal</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-orange-600 flex-shrink-0" size={18} />
                    <a href="tel:+351961313663" className="hover:text-orange-600">+351 961 313 663</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="text-orange-600 flex-shrink-0" size={18} />
                    <a href="mailto:somardistancias8.geral@gmail.com" className="hover:text-orange-600">somardistancias8.geral@gmail.com</a>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="text-orange-600 flex-shrink-0 mt-1" size={18} />
                    <span><strong>NIF:</strong> 510210848</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Dados recolhidos */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Dados Pessoais Recolhidos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Recolhemos os seguintes tipos de dados pessoais:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1. Dados Fornecidos Diretamente por Si</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li><strong>Nome completo:</strong> para identificação e comunicação</li>
                <li><strong>Endereço de email:</strong> para responder aos seus pedidos de informação</li>
                <li><strong>Número de telefone:</strong> para contacto telefónico (opcional)</li>
                <li><strong>Mensagem/Pedido:</strong> conteúdo da sua solicitação ou questão</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2. Dados Recolhidos Automaticamente</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Endereço IP:</strong> para segurança e análise de tráfego</li>
                <li><strong>Tipo de navegador e sistema operativo:</strong> para otimização do site</li>
                <li><strong>Páginas visitadas e tempo de navegação:</strong> para melhorar a experiência do utilizador</li>
                <li><strong>Cookies e tecnologias semelhantes:</strong> conforme descrito na nossa Política de Cookies</li>
              </ul>
            </section>

            {/* Finalidades */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Finalidades do Tratamento</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizamos os seus dados pessoais para as seguintes finalidades:
              </p>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Resposta a Pedidos de Contacto</h4>
                  <p className="text-sm text-gray-700">Processar e responder aos seus pedidos de orçamento, informações ou esclarecimentos sobre os nossos serviços.</p>
                  <p className="text-xs text-gray-600 mt-2"><strong>Base legal:</strong> Execução de medidas pré-contratuais (Art. 6.º, n.º 1, al. b) do RGPD)</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Melhoria dos Serviços</h4>
                  <p className="text-sm text-gray-700">Analisar o comportamento dos utilizadores para melhorar o nosso website e serviços.</p>
                  <p className="text-xs text-gray-600 mt-2"><strong>Base legal:</strong> Interesses legítimos (Art. 6.º, n.º 1, al. f) do RGPD)</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Cumprimento de Obrigações Legais</h4>
                  <p className="text-sm text-gray-700">Cumprir com obrigações legais e regulamentares aplicáveis ao nosso negócio.</p>
                  <p className="text-xs text-gray-600 mt-2"><strong>Base legal:</strong> Cumprimento de obrigação legal (Art. 6.º, n.º 1, al. c) do RGPD)</p>
                </div>
              </div>
            </section>

            {/* Partilha de dados */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Partilha de Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Os seus dados pessoais podem ser partilhados com:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Prestadores de serviços:</strong> Web3Forms (processamento de formulários de contacto), Vercel Analytics (análise de tráfego web)</li>
                <li><strong>Autoridades públicas:</strong> quando exigido por lei ou para proteger os nossos direitos</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Não vendemos, alugamos ou partilhamos os seus dados pessoais com terceiros para fins de marketing.</strong>
              </p>
            </section>

            {/* Transferências internacionais */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Transferências Internacionais</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Alguns dos nossos prestadores de serviços podem estar localizados fora do Espaço Económico Europeu (EEE). 
                Nestes casos, garantimos que existem salvaguardas adequadas em conformidade com o RGPD, tais como:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Cláusulas contratuais-tipo aprovadas pela Comissão Europeia</li>
                <li>Certificações de adequação reconhecidas pela UE</li>
              </ul>
            </section>

            {/* Prazo de conservação */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Prazo de Conservação</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Os seus dados pessoais serão conservados apenas pelo tempo necessário para as finalidades para as quais foram recolhidos:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Dados de contacto:</strong> até 2 anos após o último contacto ou até solicitar a sua eliminação</li>
                <li><strong>Dados de navegação (analytics):</strong> até 14 meses</li>
                <li><strong>Cookies:</strong> conforme especificado na nossa Política de Cookies</li>
              </ul>
            </section>

            {/* Direitos do titular */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Os Seus Direitos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Em conformidade com o RGPD, tem os seguintes direitos:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">✅ Direito de Acesso</h4>
                  <p className="text-sm text-gray-700">Consultar os dados pessoais que possuímos sobre si</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">✏️ Direito de Retificação</h4>
                  <p className="text-sm text-gray-700">Corrigir dados pessoais incorretos ou desatualizados</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🗑️ Direito ao Apagamento</h4>
                  <p className="text-sm text-gray-700">Solicitar a eliminação dos seus dados pessoais</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⏸️ Direito à Limitação</h4>
                  <p className="text-sm text-gray-700">Restringir o tratamento dos seus dados</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📦 Direito à Portabilidade</h4>
                  <p className="text-sm text-gray-700">Receber os seus dados num formato estruturado</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⛔ Direito de Oposição</h4>
                  <p className="text-sm text-gray-700">Opor-se ao tratamento dos seus dados</p>
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Como Exercer os Seus Direitos</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Para exercer qualquer um destes direitos, pode contactar-nos através de:
                </p>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <Mail className="text-orange-600" size={16} />
                    <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                      somardistancias8.geral@gmail.com
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="text-orange-600" size={16} />
                    <a href="tel:+351961313663" className="text-orange-600 hover:underline">
                      +351 961 313 663
                    </a>
                  </p>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Responderemos ao seu pedido no prazo de 30 dias.
                </p>
              </div>
            </section>

            {/* Segurança */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Segurança dos Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais contra:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Acesso não autorizado</li>
                <li>Perda, destruição ou alteração acidental</li>
                <li>Divulgação ou acesso ilegal</li>
              </ul>
            </section>

            {/* Reclamações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Direito de Reclamação</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Se considerar que o tratamento dos seus dados pessoais viola o RGPD, tem o direito de apresentar 
                uma reclamação junto da autoridade de controlo:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <p className="font-semibold text-gray-900 mb-2">Comissão Nacional de Proteção de Dados (CNPD)</p>
                <p className="text-sm text-gray-700">Av. D. Carlos I, 134, 1.º</p>
                <p className="text-sm text-gray-700">1200-651 Lisboa, Portugal</p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Tel:</strong> +351 213 928 400<br />
                  <strong>Email:</strong> geral@cnpd.pt<br />
                  <strong>Website:</strong> <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">www.cnpd.pt</a>
                </p>
              </div>
            </section>

            {/* Alterações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Alterações à Política</h2>
              <p className="text-gray-700 leading-relaxed">
                Reservamo-nos o direito de atualizar esta Política de Privacidade periodicamente. Qualquer alteração 
                será publicada nesta página com a data da última atualização. Recomendamos que consulte regularmente 
                esta política para se manter informado sobre como protegemos os seus dados.
              </p>
            </section>

            {/* Contacto */}
            <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contacto</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para quaisquer questões sobre esta Política de Privacidade ou sobre o tratamento dos seus dados pessoais, 
                entre em contacto connosco:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="text-orange-600" size={20} />
                  <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline font-medium">
                    somardistancias8.geral@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-orange-600" size={20} />
                  <a href="tel:+351961313663" className="text-orange-600 hover:underline font-medium">
                    +351 961 313 663
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Estrada de Santa Eulália, Horta Preta, Casal das Oliveiras<br />
                    2670-351 Loures, Portugal
                  </span>
                </div>
              </div>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Button
              onClick={() => navigate('/')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
